import { BuiltInTooltip } from '../../charts/tooltip/types';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';

const ONE_MINUTE = 60_000;

export const VOLUME = 'volume';
export const RATE = 'rate';
export const UTIL = 'util';

export const latencyDef = (average, minimum) => ({
    tooltip: BuiltInTooltip,
    statusMetric: 'siteLatencyStatus',
    chartTrends: [
        {
            chartType: 'area',
            key: 'latency',
            value: 'avg',
            label: average,
            color: '#7cb5ec',
            units: 'ms',
            multiplier: 1,
        },
        {
            chartType: 'spline',
            key: 'latency',
            value: 'min',
            label: minimum,
            color: '#434348',
            units: 'ms',
            multiplier: 1,
        },
    ],
});

const volumeMetricBitsSecondsMapping = (value, granularity) => (value * granularity) / 8000;

export const bandwidthVolumeDef = (input, output) => ({
    tooltip: BuiltInTooltip,
    statusMetric: 'statusInterfaceUtil',
    stacking: true,
    metricMapping: volumeMetricBitsSecondsMapping,
    chartTrends: [
        {
            formatter: FORMATTER_TYPES.BYTERATE,
            chartType: 'areaspline',
            key: 'inBitsPerSec',
            value: 'avg',
            label: input,
            color: '#7cb5ec',
        },
        {
            formatter: FORMATTER_TYPES.BYTERATE,
            chartType: 'areaspline',
            key: 'outBitsPerSec',
            value: 'avg',
            label: output,
            color: '#434348',
            multiplier: 1,
        },
    ],
});

export const bandwidthRateDef = (input, output) => ({
    tooltip: BuiltInTooltip,
    statusMetric: 'statusInterfaceUtil',
    chartTrends: [
        {
            formatter: FORMATTER_TYPES.BITRATE,
            chartType: 'spline',
            key: 'inBitsPerSec',
            value: 'avg',
            label: input,
            color: '#7cb5ec',
            multiplier: 1,
        },
        {
            formatter: FORMATTER_TYPES.BITRATE,
            chartType: 'spline',
            key: 'outBitsPerSec',
            value: 'avg',
            label: output,
            color: '#434348',
            multiplier: 1,
        },
    ],
});

export const bandwidthUtilDef = (input, output) => ({
    tooltip: BuiltInTooltip,
    statusMetric: 'statusInterfaceUtil',
    chartTrends: [
        {
            units: '%',
            chartType: 'spline',
            key: 'inNetworkUtil',
            value: 'avg',
            label: input,
            color: '#7cb5ec',
            multiplier: 1,
        },
        {
            units: '%',
            chartType: 'spline',
            key: 'outNetworkUtil',
            value: 'avg',
            label: output,
            color: '#434348',
            multiplier: 1,
        },
    ],
});

export const bandWidthFn = (type, input, output) => {
    switch (type) {
        case VOLUME:
            return bandwidthVolumeDef(input, output);
        case RATE:
            return bandwidthRateDef(input, output);
        default:
            return bandwidthUtilDef(input, output);
    }
};

const volumeMetricMinutesMapping = (value, granularity) => (value * granularity) / ONE_MINUTE;

export const discardsErrorsVolumnDef = (discardsIn, discardsOut, errorsIn, errorsOut) => ({
    tooltip: BuiltInTooltip,
    statusMetric: 'statusInterfaceErrors',
    statusMetricBackup: 'statusInterfaceDiscards',
    metricMapping: volumeMetricMinutesMapping,
    stacking: true,
    chartTrends: [
        {
            formatter: FORMATTER_TYPES.LOG,
            chartType: 'areaspline',
            key: 'siteDiscardsIn',
            value: 'avg',
            label: discardsIn,
            color: '#7cb5ec',
            units: ' Discards',
            multiplier: 0,
        },
        {
            formatter: FORMATTER_TYPES.LOG,
            chartType: 'areaspline',
            key: 'siteDiscardsOut',
            value: 'avg',
            label: discardsOut,
            color: '#434348',
            units: ' Discards',
            multiplier: 0,
        },
        {
            formatter: FORMATTER_TYPES.LOG,
            chartType: 'areaspline',
            key: 'siteErrorsIn',
            value: 'avg',
            label: errorsIn,
            color: '#90ed7d',
            units: ' Errors',
            multiplier: 0,
        },
        {
            formatter: FORMATTER_TYPES.LOG,
            chartType: 'areaspline',
            key: 'siteErrorsOut',
            value: 'avg',
            label: errorsOut,
            color: '#f7a35c',
            units: ' Errors',
            multiplier: 0,
        },
    ],
});

export const discardsErrorsRateDef = (discardsIn, discardsOut, errorsIn, errorsOut) => ({
    tooltip: BuiltInTooltip,
    statusMetric: 'statusInterfaceErrors',
    statusMetricBackup: 'statusInterfaceDiscards',
    chartTrends: [
        {
            chartType: 'spline',
            key: 'siteDiscardsIn',
            value: 'avg',
            label: discardsIn,
            color: '#7cb5ec',
            units: ' Discards/min',
            multiplier: 1,
        },
        {
            chartType: 'spline',
            key: 'siteDiscardsOut',
            value: 'avg',
            label: discardsOut,
            color: '#434348',
            units: ' Discards/min',
            multiplier: 1,
        },
        {
            chartType: 'spline',
            key: 'siteErrorsIn',
            value: 'avg',
            label: errorsIn,
            color: '#90ed7d',
            units: ' Errors/min',
            multiplier: 1,
        },
        {
            chartType: 'spline',
            key: 'siteErrorsOut',
            value: 'avg',
            label: errorsOut,
            color: '#f7a35c',
            units: ' Errors/min',
            multiplier: 1,
        },
    ],
});

export const discardsErrorsPercentDef = (discardsIn, discardsOut, errorsIn, errorsOut) => ({
    tooltip: BuiltInTooltip,
    statusMetric: 'statusInterfaceErrors',
    statusMetricBackup: 'statusInterfaceDiscards',
    chartTrends: [
        {
            chartType: 'spline',
            key: 'siteDiscardRateInPerc',
            value: 'avg',
            label: discardsIn,
            color: '#7cb5ec',
            units: '%',
            multiplier: 1,
        },
        {
            chartType: 'spline',
            key: 'siteDiscardRateOutPerc',
            value: 'avg',
            label: discardsOut,
            color: '#434348',
            units: '%',
            multiplier: 1,
        },
        {
            chartType: 'spline',
            key: 'siteErrorRateInPerc',
            value: 'avg',
            label: errorsIn,
            color: '#90ed7d',
            units: '%',
            multiplier: 1,
        },
        {
            chartType: 'spline',
            key: 'siteErrorRateOutPerc',
            value: 'avg',
            label: errorsOut,
            color: '#f7a35c',
            units: '%',
            multiplier: 1,
        },
    ],
});

export const discardsErrorsFn = (type, discardsIn, discardsOut, errorsIn, errorsOut) => {
    switch (type) {
        case VOLUME:
            return discardsErrorsVolumnDef(discardsIn, discardsOut, errorsIn, errorsOut);
        case RATE:
            return discardsErrorsRateDef(discardsIn, discardsOut, errorsIn, errorsOut);
        default:
            return discardsErrorsPercentDef(discardsIn, discardsOut, errorsIn, errorsOut);
    }
};
